import { Redirect, Route, Switch } from 'react-router-dom';

import { AiChatboxShell } from '@/ai/components/ai-chatbox-shell';
import { AlertsModule } from '@/alerts';
import { BenchmarkPage } from '@/benchmark/pages/benchmark';
import { CreateSitePage } from '@/documents/view/pages/create-site';
import { SiteDocumentsPage } from '@/documents/view/pages/site-documents';
import { PageUrlKey } from '@/entities/page-url-key';
import { SystemLogsPage } from '@/modules/system-logs';
import { WhiteLabelServiceContentPage } from '@/pages/white-label-service-content';
import { SitesPage } from '@/project-management/pages/sites';
import { SiteMap } from '@/site-map/pages/site-map';
import { SiteviewConfiguratorPage } from '@/siteview-configurator/pages/siteview-configurator.page';
import { TasksPage } from '@/task-manager/pages/tasks';
import { WarrantyDetailsPage } from '@/warranty/view/pages/warranty-details';

import { Shell } from '../components/layout/shell';
import { UserRole } from '../entities/user-role';
import { useFeatureFlag } from '../hooks/use-feature-flag';
import { useMe } from '../hooks/use-me';
import { GlobalCommunicationPage } from '../modules/communication/pages/global-communication';
import { AlertsExportPage } from '../pages/alerts-export';
import { ChangelogPage } from '../pages/changelog';
import { CustomDashboardsPage } from '../pages/custom-dashboards';
import { DataCompatibilityPage } from '../pages/data-compatibility';
import { HomePage } from '../pages/home';
import { WarrantyPageLegacy } from '../pages/warranty';

import { RoutesAccount } from './routes-account';
import { RoutesAsset } from './routes-asset';
import { ThirdPartyRoutes } from './third-party-routes';

export function Routes() {
  const featureFlag = useFeatureFlag();
  const me = useMe();

  if (me.role === UserRole.THIRD_PARTY) {
    return <ThirdPartyRoutes />;
  }

  return (
    <AiChatboxShell>
      <Switch>
        <Route
          exact
          path="/"
          component={function () {
            if (!featureFlag('landingpage')) {
              return <Redirect to={`/${PageUrlKey.DOCUMENTS}`} />;
            }

            return (
              <Shell>
                <HomePage />
              </Shell>
            );
          }}
        />
        {featureFlag('assets') ? (
          <Route
            exact
            path={`/${PageUrlKey.BENCHMARK}`}
            component={function () {
              return (
                <Shell>
                  <BenchmarkPage />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('alerts') ? (
          <Route
            exact
            path={`/${PageUrlKey.ALERTS}`}
            component={function AlertsGateway() {
              return (
                <Shell>
                  <AlertsModule />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('systemLogs') ? (
          <Route
            exact
            path={`/${PageUrlKey.SYSTEM_LOGS}`}
            component={function SystemLogsGateway() {
              return (
                <Shell>
                  <SystemLogsPage />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('records') ? (
          <Route
            exact
            path={`/${PageUrlKey.DOCUMENTS}`}
            component={function DocumentsGateway() {
              return (
                <Shell>
                  <SiteDocumentsPage />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('recordsCreate') ? (
          <Route
            exact
            path="/new-asset"
            component={function () {
              return (
                <Shell>
                  <CreateSitePage />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('alerts') ? (
          <Route
            exact
            path={`/${PageUrlKey.ALERTS_EXPORT}`}
            component={function () {
              return (
                <Shell>
                  <AlertsExportPage />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('taskmanager') ? (
          <Route
            exact
            path={`/${PageUrlKey.TASKS}`}
            component={function () {
              return (
                <Shell>
                  <TasksPage />
                </Shell>
              );
            }}
          />
        ) : null}
        {me.whiteLabel ? (
          <Route
            exact
            path="/service-content"
            component={function () {
              return (
                <Shell>
                  <WhiteLabelServiceContentPage />
                </Shell>
              );
            }}
          />
        ) : null}
        <Route
          exact
          path={`/${PageUrlKey.CHANGELOG}`}
          component={() => (
            <Shell>
              <ChangelogPage />
            </Shell>
          )}
        />
        {featureFlag('customDashboards') ? (
          <Route
            path="/custom"
            component={function () {
              return (
                <Shell>
                  <CustomDashboardsPage />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('sitemap') ? (
          <Route
            exact
            path={`/${PageUrlKey.SITE_MAP}`}
            component={function () {
              return (
                <Shell>
                  <SiteMap />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('warranty') &&
        !featureFlag('warrantyV2') &&
        me.config.type === 'mobility' ? (
          <Route
            exact
            path={`/${PageUrlKey.WARRANTY}`}
            component={function () {
              return (
                <Shell>
                  <WarrantyPageLegacy />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('warranty') &&
        featureFlag('warrantyV2') &&
        me.config.type === 'mobility' ? (
          <Route
            exact
            path={`/${PageUrlKey.WARRANTY}`}
            component={function () {
              return (
                <Shell>
                  <WarrantyDetailsPage />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('dataCompatibility') ? (
          <Route
            exact
            path={`/${PageUrlKey.DATA_COMPATIBILITY}`}
            component={function () {
              return (
                <Shell>
                  <DataCompatibilityPage />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('communication') ? (
          <Route
            exact
            path={`/${PageUrlKey.COMMUNICATION}`}
            component={() => (
              <Shell>
                <GlobalCommunicationPage />
              </Shell>
            )}
          />
        ) : null}
        {featureFlag('projectManagement') ? (
          <Route
            exact
            path={`/${PageUrlKey.PROJECTS}`}
            component={function () {
              return (
                <Shell>
                  <SitesPage />
                </Shell>
              );
            }}
          />
        ) : null}
        {featureFlag('siteviewConfigurator') ? (
          <Route
            exact
            path={`/${PageUrlKey.SITEVIEW_CONFIGURATOR}`}
            component={function () {
              return (
                <Shell>
                  <SiteviewConfiguratorPage />
                </Shell>
              );
            }}
          />
        ) : null}
        <Route
          path={
            me.config.type === 'mobility'
              ? '/vehicle/:siteId'
              : '/asset/:siteId'
          }
          component={RoutesAsset}
        />
        <Route path="/account" component={RoutesAccount} />
        <Redirect to="/" />
      </Switch>
    </AiChatboxShell>
  );
}
